import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { loginUser, resetErrorState } from "../../slices/session";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { getMicrosoftAuthorizeUrl } from "../../api/microsoft";
import {
  MICROSOFT_OAUTH_INTENT_KEY,
  MICROSOFT_OAUTH_RETURN_PATH_KEY,
} from "../../utils/microsoftOAuth";
import { resolvePostLoginPath } from "../../utils/authReturnUrl";

/**
 * Login — split-screen design (dark brand panel + light form panel).
 * Wait/feedback states follow the Promang loading & motion system
 * (PROMANG_LOADING_SYSTEM.md): full-screen cold-boot loader (§4.1) and the
 * idle → loading → done submit button (§4.4). One motion language, dark tokens.
 */

// Dark-theme motion tokens (PROMANG_LOADING_SYSTEM §2), aligned with the brand panel.
const T = {
  surface: "#070b18",
  surface2: "#121b2e",
  border: "#1e2a45",
  accent: "#3b7bff",
  accent2: "#6e9cff",
  glow: "#35d6ff",
  ok: "#16a86b",
  muted: "#8693b5",
  faint: "#5a678c",
};

// Light form-panel palette (from the source design).
const F = {
  fg: "#13182a",
  sub: "#76809a",
  label: "#3a4258",
  line: "#d9deea",
  lineFocus: "#2d62cf",
  link: "#2d62cf",
  remember: "#5a6680",
  eye: "#9099ac",
  bad: "#e54848",
  btn: "#13182a",
  btnHover: "#1f2742",
};

// Microsoft sign-in is hidden for now (kept in code, not removed).
const SHOW_MICROSOFT_LOGIN = false;

const BOOT_STEPS = [
  "auth.boot.initializing",
  "auth.boot.syncing",
  "auth.boot.securing",
  "auth.boot.finalizing",
];

// Scoped keyframes + :focus / :hover / responsive rules. Animates only
// transform/opacity, honors prefers-reduced-motion (loading system §6).
const STYLE = `
@keyframes lgSpin{to{transform:rotate(360deg)}}
@keyframes lgSpinRev{to{transform:rotate(-360deg)}}
@keyframes lgFloaty{0%,100%{transform:translateY(0)}50%{transform:translateY(-5px)}}
@keyframes lgGlow{0%,100%{opacity:.35;transform:scale(1)}50%{opacity:.85;transform:scale(1.08)}}
@keyframes lgSheen{0%{transform:translateX(-120%)}100%{transform:translateX(220%)}}
@keyframes lgPop{0%{transform:scale(0)}60%{transform:scale(1.2)}100%{transform:scale(1)}}
@keyframes lgFadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
@keyframes lgTwinkle{0%,100%{opacity:.18;transform:scale(.85)}50%{opacity:1;transform:scale(1.15)}}
@keyframes lgDash{to{stroke-dashoffset:-200}}
@keyframes lgFloatB{0%,100%{transform:translate(0,0)}50%{transform:translate(-30px,28px)}}
.lg-input{transition:border-color .15s}
.lg-input:focus{border-color:${F.lineFocus}!important}
.lg-input:-webkit-autofill,
.lg-input:-webkit-autofill:hover,
.lg-input:-webkit-autofill:focus,
.lg-input:-webkit-autofill:active{
  -webkit-text-fill-color:${F.fg}!important;
  -webkit-box-shadow:0 0 0 1000px #ffffff inset!important;
  box-shadow:0 0 0 1000px #ffffff inset!important;
  caret-color:${F.fg};
  font:500 15px 'Plus Jakarta Sans',sans-serif;
  transition:background-color 99999s ease-in-out 0s,border-color .15s;
}
.lg-eye{transition:color .15s}
.lg-eye:hover{color:${F.lineFocus}!important}
.lg-forgot:hover{text-decoration:underline!important}
.lg-primary{transition:background .12s,transform .12s}
.lg-primary:not(:disabled):hover{background:${F.btnHover}!important;transform:translateY(-1px)}
.lg-ms{transition:border-color .15s,background .15s}
.lg-ms:not(:disabled):hover{border-color:#c4cde0!important;background:#f7f9fd!important}
@media (max-width:900px){
  .lg-root{flex-direction:column!important}
  .lg-brand{flex:none!important;padding:34px 28px 28px!important;min-height:auto!important}
  .lg-brand-copy{flex:none!important;justify-content:flex-start!important;margin-top:20px!important;max-width:none!important}
  .lg-brand-copy h2{font-size:23px!important;margin-bottom:7px!important;letter-spacing:-.4px!important}
  .lg-brand-copy p{font-size:13.5px!important}
  .lg-brand-footer{display:none!important}
  .lg-form{width:100%!important;flex:1!important;padding:34px 28px 48px!important;justify-content:flex-start!important}
}
@media (prefers-reduced-motion: reduce){
  .lg-anim{animation:none!important}
}
`;

function EyeIcon({ off }) {
  return off ? (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c6.5 0 10 8 10 8a13 13 0 0 1-1.67 2.68" />
      <path d="M6.6 6.6A13 13 0 0 0 2 12s3.5 8 10 8a9.1 9.1 0 0 0 5.4-1.6" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </svg>
  ) : (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 12s3.5-8 10-8 10 8 10 8-3.5 8-10 8-10-8-10-8Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function CheckIcon({ size = 17, stroke = "#fff", width = 2.6 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M5 12.5l4.5 4.5L19 6.5"
        stroke={stroke}
        strokeWidth={width}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Full-screen cold-boot loader — PROMANG_LOADING_SYSTEM §4.1 (dark tokens). */
function BootScreen() {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(8);

  useEffect(() => {
    const id = setInterval(() => {
      setProgress((p) => {
        const next = p + Math.random() * 14 + 5;
        return next >= 100 ? 100 : next;
      });
    }, 180);
    return () => clearInterval(id);
  }, []);

  const pct = Math.round(progress);
  const stepIndex =
    pct < 25 ? 0 : pct < 55 ? 1 : pct < 85 ? 2 : 3;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        display: "grid",
        placeItems: "center",
        overflow: "hidden",
        background:
          "radial-gradient(120% 120% at 20% 10%,#16224a 0%,#0b1226 55%,#070b18 100%)",
        fontFamily: "Manrope, sans-serif",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(120,150,255,.06) 1px,transparent 1px),linear-gradient(90deg,rgba(120,150,255,.06) 1px,transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />
      <div
        className="lg-anim"
        style={{
          position: "absolute",
          width: 620,
          height: 620,
          borderRadius: "50%",
          background:
            "radial-gradient(circle,rgba(59,123,255,.26),transparent 62%)",
          filter: "blur(8px)",
          animation: "lgGlow 3.4s ease-in-out infinite",
        }}
      />
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <div
          style={{
            position: "relative",
            width: 128,
            height: 128,
            display: "grid",
            placeItems: "center",
            marginBottom: 34,
          }}
        >
          <div
            className="lg-anim"
            style={{
              position: "absolute",
              width: 128,
              height: 128,
              borderRadius: "50%",
              border: `1px dashed ${T.border}`,
              animation: "lgSpinRev 14s linear infinite",
            }}
          />
          <div
            className="lg-anim"
            style={{
              position: "absolute",
              width: 96,
              height: 96,
              borderRadius: "50%",
              background: `conic-gradient(from 0deg,transparent 0deg,${T.accent} 300deg,${T.glow})`,
              WebkitMask:
                "radial-gradient(farthest-side,transparent calc(100% - 4px),#000 0)",
              mask: "radial-gradient(farthest-side,transparent calc(100% - 4px),#000 0)",
              animation: "lgSpin 1.05s linear infinite",
              filter: `drop-shadow(0 0 10px ${T.accent})`,
            }}
          />
          <div
            className="lg-anim"
            style={{
              width: 60,
              height: 60,
              borderRadius: 17,
              background: `linear-gradient(135deg,${T.accent},${T.accent2})`,
              display: "grid",
              placeItems: "center",
              animation: "lgFloaty 2.6s ease-in-out infinite",
              boxShadow: `0 10px 26px rgba(59,123,255,.5)`,
            }}
          >
            <div
              className="lg-anim"
              style={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                border: "3px solid rgba(255,255,255,.95)",
                borderTopColor: "transparent",
                borderRightColor: "transparent",
                animation: "lgSpin 1.05s linear infinite",
              }}
            />
          </div>
        </div>

        <div
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 700,
            fontSize: 22,
            letterSpacing: ".34em",
            color: "#eaf0ff",
            marginBottom: 8,
          }}
        >
          PROMANG
        </div>
        <div
          role="status"
          aria-live="polite"
          style={{
            fontFamily: "ui-monospace, monospace",
            fontSize: 13,
            color: T.muted,
            minHeight: 18,
            marginBottom: 26,
          }}
        >
          {t(BOOT_STEPS[stepIndex])}
        </div>

        <div style={{ width: 300 }}>
          <div
            style={{
              height: 5,
              borderRadius: 99,
              background: T.surface2,
              border: `1px solid ${T.border}`,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                borderRadius: 99,
                background: `linear-gradient(90deg,${T.accent},${T.glow})`,
                boxShadow: `0 0 12px ${T.accent}`,
                width: `${pct}%`,
                transition: "width .18s linear",
              }}
            />
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 10,
              fontFamily: "ui-monospace, monospace",
              fontSize: 11,
              color: T.faint,
            }}
          >
            <span>{t("auth.boot.secure_session")}</span>
            <span>{pct}%</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Animated constellation + twinkling stars for the dark brand panel. */
function BrandBackdrop() {
  const stars = useMemo(() => {
    const out = [];
    for (let i = 0; i < 60; i++) {
      out.push({
        left: (Math.random() * 100).toFixed(2),
        top: (Math.random() * 100).toFixed(2),
        size: (1 + Math.random() * 2.2).toFixed(1),
        op: (0.15 + Math.random() * 0.75).toFixed(2),
        dur: (2 + Math.random() * 4).toFixed(1),
        delay: (Math.random() * 4).toFixed(1),
      });
    }
    return out;
  }, []);

  const constellation = useMemo(() => {
    const groups = [
      [
        [60, 90],
        [150, 60],
        [225, 120],
        [165, 195],
        [80, 165],
      ],
      [
        [300, 50],
        [395, 110],
        [330, 185],
      ],
      [
        [230, 300],
        [330, 330],
        [285, 410],
        [190, 380],
      ],
    ];
    const lines = [];
    let k = 0;
    groups.forEach((g) => {
      for (let i = 0; i < g.length; i++) {
        const p = g[i];
        const q = g[(i + 1) % g.length];
        lines.push({
          key: `l${k++}`,
          x1: p[0],
          y1: p[1],
          x2: q[0],
          y2: q[1],
          dur: (7 + Math.random() * 4).toFixed(1),
        });
      }
    });
    const dots = groups.flat().map((p, i) => ({
      key: `c${i}`,
      cx: p[0],
      cy: p[1],
      dur: (2 + Math.random() * 3).toFixed(1),
      delay: (Math.random() * 3).toFixed(1),
    }));
    return { lines, dots };
  }, []);

  return (
    <>
      <div style={{ position: "absolute", inset: 0 }}>
        <svg
          viewBox="0 0 480 460"
          preserveAspectRatio="xMidYMid slice"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
          aria-hidden="true"
        >
          {constellation.lines.map((l) => (
            <line
              key={l.key}
              x1={l.x1}
              y1={l.y1}
              x2={l.x2}
              y2={l.y2}
              stroke="rgba(150,180,255,.28)"
              strokeWidth={1}
              strokeDasharray="4 5"
              className="lg-anim"
              style={{ animation: `lgDash ${l.dur}s linear infinite` }}
            />
          ))}
          {constellation.dots.map((d) => (
            <circle
              key={d.key}
              cx={d.cx}
              cy={d.cy}
              r={2.6}
              fill="rgba(190,212,255,.9)"
              className="lg-anim"
              style={{
                animation: `lgTwinkle ${d.dur}s ease-in-out ${d.delay}s infinite`,
              }}
            />
          ))}
        </svg>
      </div>
      <div style={{ position: "absolute", inset: 0 }}>
        {stars.map((s, i) => (
          <div
            key={i}
            className="lg-anim"
            style={{
              position: "absolute",
              left: `${s.left}%`,
              top: `${s.top}%`,
              width: `${s.size}px`,
              height: `${s.size}px`,
              borderRadius: "50%",
              background: `rgba(190,212,255,${s.op})`,
              animation: `lgTwinkle ${s.dur}s ease-in-out ${s.delay}s infinite`,
            }}
          />
        ))}
      </div>
      <div
        className="lg-anim"
        style={{
          position: "absolute",
          bottom: -120,
          left: -70,
          width: 380,
          height: 380,
          borderRadius: "50%",
          background:
            "radial-gradient(circle,rgba(91,141,239,.28),transparent 70%)",
          filter: "blur(16px)",
          animation: "lgFloatB 16s ease-in-out infinite",
        }}
      />
    </>
  );
}

function Login() {
  const emailRef = useRef();
  const errorMessages = useSelector((state) => state.session.errorMessages);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const postLoginPath = resolvePostLoginPath({
    searchParams,
    from: location.state?.from,
  });

  const [booting, setBooting] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  // Submit button state machine: idle → loading → success (loading system §4.4).
  const [btnState, setBtnState] = useState("idle");
  const [microsoftLoading, setMicrosoftLoading] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setBooting(false), 2000);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    emailRef?.current?.focus();
    if (errorMessages.length > 0) {
      const message = errorMessages.map((entry) =>
        typeof entry === "string" && entry.startsWith("auth.") ? t(entry) : entry
      );
      toast.error(message.join(" "));
      dispatch(resetErrorState());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formik = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema: Yup.object({
      email: Yup.string()
        .email(t("auth.login.email_invalid", { defaultValue: "Must be a valid email" }))
        .max(255)
        .required(t("auth.login.email_required", { defaultValue: "Email is required" })),
      password: Yup.string()
        .max(255)
        .required(t("auth.login.password_required", { defaultValue: "Password is required" })),
    }),
    onSubmit: async (values, helpers) => {
      setBtnState("loading");
      try {
        const response = await dispatch(
          loginUser({ email: values.email, password: values.password })
        );
        if (!response.error) {
          setBtnState("success");
          setTimeout(() => {
            navigate(postLoginPath, { replace: true });
          }, 650);
        } else {
          const rawMessage =
            (response.payload &&
              typeof response.payload === "object" &&
              "error" in response.payload &&
              typeof response.payload.error === "string" &&
              response.payload.error) ||
            t("auth.login.invalid_credentials");
          const message = rawMessage.startsWith("auth.") ? t(rawMessage) : rawMessage;
          toast.error(message);
          setBtnState("idle");
        }
      } catch (err) {
        helpers.setStatus({ success: false });
        toast.error(err?.message || t("auth.login.invalid_credentials"));
        setBtnState("idle");
      } finally {
        helpers.setSubmitting(false);
      }
    },
  });

  const startMicrosoftLogin = async () => {
    setMicrosoftLoading(true);
    try {
      const data = await getMicrosoftAuthorizeUrl("login");
      const url = data?.url;
      if (!url) {
        toast.error(
          t("microsoft.toast.start_failed", {
            defaultValue: "Kunne ikke starte Microsoft-innlogging",
          })
        );
        return;
      }
      sessionStorage.setItem(MICROSOFT_OAUTH_INTENT_KEY, "login");
      sessionStorage.setItem(MICROSOFT_OAUTH_RETURN_PATH_KEY, postLoginPath);
      window.location.assign(url);
    } catch (e) {
      const msg = e?.response?.data?.errors?.[0];
      toast.error(
        msg ||
          t("microsoft.toast.start_failed", {
            defaultValue: "Kunne ikke starte Microsoft-innlogging",
          })
      );
    } finally {
      setMicrosoftLoading(false);
    }
  };

  if (booting) {
    return (
      <>
        <style>{STYLE}</style>
        <BootScreen />
      </>
    );
  }

  const emailError = formik.touched.email && formik.errors.email;
  const passwordError = formik.touched.password && formik.errors.password;
  const busy = btnState !== "idle";

  const fieldStyle = (hasError) => ({
    width: "100%",
    padding: "13px 0",
    font: "500 15px 'Plus Jakarta Sans', sans-serif",
    color: F.fg,
    background: "transparent",
    border: "none",
    borderBottom: `1.5px solid ${hasError ? F.bad : F.line}`,
    outline: "none",
  });

  const labelStyle = {
    font: "600 12.5px 'Plus Jakarta Sans', sans-serif",
    color: F.label,
    marginBottom: 7,
    display: "block",
    letterSpacing: ".06em",
    textTransform: "uppercase",
  };

  const errorTextStyle = {
    font: "500 12px 'Plus Jakarta Sans', sans-serif",
    color: F.bad,
    marginTop: 6,
  };

  return (
    <>
      <style>{STYLE}</style>
      <div
        className="lg-root"
        style={{
          minHeight: "100vh",
          width: "100%",
          display: "flex",
          background: "#ffffff",
          fontFamily: "Manrope, sans-serif",
        }}
      >
        {/* brand panel (dark, left on desktop, compact header on mobile) */}
        <div
          className="lg-brand"
          style={{
            flex: 1,
            minWidth: 0,
            position: "relative",
            overflow: "hidden",
            background:
              "radial-gradient(120% 120% at 20% 10%,#16224a 0%,#0b1226 55%,#070b18 100%)",
            display: "flex",
            flexDirection: "column",
            padding: "56px 56px",
          }}
        >
          <BrandBackdrop />
          <div
            style={{
              position: "relative",
              font: "700 26px/1 'Space Grotesk', sans-serif",
              letterSpacing: "-.5px",
            }}
          >
            <span style={{ color: "#7fb0ff" }}>Pro</span>
            <span style={{ color: "#9fb1cb", fontWeight: 500 }}>mang</span>
          </div>
          <div
            className="lg-brand-copy"
            style={{
              position: "relative",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              maxWidth: 460,
            }}
          >
            <h2
              style={{
                font: "800 clamp(34px,3.4vw,46px)/1.12 Manrope, sans-serif",
                color: "#f3f6ff",
                margin: "0 0 18px",
                letterSpacing: "-.8px",
              }}
            >
              {t("auth.login.brand_heading")}
            </h2>
            <p
              style={{
                font: "500 17px/1.6 Manrope, sans-serif",
                color: "#9aa7c4",
                margin: 0,
                maxWidth: 380,
              }}
            >
              {t("auth.login.brand_subtitle")}
            </p>
          </div>
          <div
            className="lg-brand-footer"
            style={{
              position: "relative",
              font: "500 12px Manrope, sans-serif",
              color: "#5e6a85",
            }}
          >
            {t("auth.login.brought_by")}{" "}
            <a
              href="https://primetek.no"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#7f8db0", textDecoration: "none" }}
            >
              Primetek AS
            </a>
          </div>
        </div>

        {/* form panel (light, right) */}
        <div
          className="lg-form"
          style={{
            width: 520,
            maxWidth: "100%",
            flex: "none",
            padding: "56px 64px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <form
            onSubmit={formik.handleSubmit}
            style={{
              width: "100%",
              maxWidth: 360,
              margin: "0 auto",
              animation: "lgFadeUp .4s cubic-bezier(.2,.9,.3,1)",
            }}
            className="lg-anim"
          >
            <h1
              style={{
                font: "800 34px/1.1 'Plus Jakarta Sans', sans-serif",
                color: F.fg,
                margin: "0 0 7px",
                letterSpacing: "-.6px",
              }}
            >
              {t("auth.login.title")}
            </h1>
            <p
              style={{
                font: "500 14.5px/1.5 'Plus Jakarta Sans', sans-serif",
                color: F.sub,
                margin: "0 0 34px",
              }}
            >
              {t("auth.login.subtitle")}
            </p>

            <label htmlFor="email" style={labelStyle}>
              {t("auth.login.email_label")}
            </label>
            <input
              ref={emailRef}
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              className="lg-input"
              placeholder={t("auth.login.email_placeholder")}
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={busy}
              style={{ ...fieldStyle(emailError), marginBottom: emailError ? 6 : 24 }}
            />
            {emailError && (
              <div style={{ ...errorTextStyle, marginBottom: 18 }}>{formik.errors.email}</div>
            )}

            <label htmlFor="password" style={labelStyle}>
              {t("auth.login.password_label")}
            </label>
            <div style={{ position: "relative", marginBottom: passwordError ? 6 : 20 }}>
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                className="lg-input"
                placeholder="••••••••"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={busy}
                style={{ ...fieldStyle(passwordError), padding: "13px 40px 13px 0" }}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={
                  showPassword
                    ? t("auth.login.hide_password")
                    : t("auth.login.show_password")
                }
                className="lg-eye"
                style={{
                  position: "absolute",
                  right: 0,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  padding: 6,
                  cursor: "pointer",
                  color: F.eye,
                  display: "flex",
                }}
              >
                <EyeIcon off={showPassword} />
              </button>
            </div>
            {passwordError && (
              <div style={{ ...errorTextStyle, marginBottom: 14 }}>{formik.errors.password}</div>
            )}

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 30,
              }}
            >
              <button
                type="button"
                onClick={() => setRemember((v) => !v)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 9,
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  font: "600 13px 'Plus Jakarta Sans', sans-serif",
                  color: F.remember,
                }}
              >
                {remember ? (
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 5,
                      background: F.lineFocus,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#fff"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  </span>
                ) : (
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 5,
                      border: "1.8px solid #c4cde0",
                      background: "#fff",
                    }}
                  />
                )}
                {t("auth.login.remember_me")}
              </button>
              <a
                href="mailto:support@primetek.no"
                className="lg-forgot"
                style={{
                  font: "600 13px 'Plus Jakarta Sans', sans-serif",
                  color: F.link,
                  textDecoration: "none",
                }}
              >
                {t("auth.login.forgot_password")}
              </a>
            </div>

            <button
              type="submit"
              disabled={busy}
              className="lg-primary"
              style={{
                position: "relative",
                overflow: "hidden",
                width: "100%",
                padding: 15,
                font: "700 15px 'Plus Jakarta Sans', sans-serif",
                color: "#fff",
                background: btnState === "success" ? T.ok : F.btn,
                border: "none",
                borderRadius: 10,
                cursor: busy ? "default" : "pointer",
              }}
            >
              {btnState === "loading" && (
                <span
                  style={{
                    position: "absolute",
                    inset: 0,
                    overflow: "hidden",
                    borderRadius: 10,
                  }}
                >
                  <span
                    className="lg-anim"
                    style={{
                      position: "absolute",
                      top: 0,
                      bottom: 0,
                      width: "40%",
                      background:
                        "linear-gradient(90deg,transparent,rgba(255,255,255,.22),transparent)",
                      animation: "lgSheen 1.1s linear infinite",
                    }}
                  />
                </span>
              )}
              <span
                style={{
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                }}
              >
                {btnState === "loading" && (
                  <span
                    className="lg-anim"
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      border: "2.4px solid rgba(255,255,255,.45)",
                      borderTopColor: "#fff",
                      animation: "lgSpin .7s linear infinite",
                    }}
                  />
                )}
                {btnState === "success" && (
                  <span className="lg-anim" style={{ display: "flex", animation: "lgPop .35s ease" }}>
                    <CheckIcon />
                  </span>
                )}
                {btnState === "loading"
                  ? t("auth.login.submitting")
                  : btnState === "success"
                    ? t("auth.login.success")
                    : t("auth.login.submit")}
              </span>
            </button>

            {SHOW_MICROSOFT_LOGIN && (
              <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    margin: "22px 0",
                  }}
                >
                  <div style={{ height: 1, flex: 1, background: "#e6e9f2" }} />
                  <span
                    style={{
                      font: "600 12px 'Plus Jakarta Sans', sans-serif",
                      color: "#9aa3ba",
                    }}
                  >
                    {t("microsoft.login.or", { defaultValue: "eller" })}
                  </span>
                  <div style={{ height: 1, flex: 1, background: "#e6e9f2" }} />
                </div>

                <button
                  type="button"
                  onClick={startMicrosoftLogin}
                  disabled={microsoftLoading || busy}
                  className="lg-ms"
                  style={{
                    width: "100%",
                    padding: 14,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 10,
                    font: "600 14px 'Plus Jakarta Sans', sans-serif",
                    color: F.fg,
                    background: "#fff",
                    border: "1.5px solid #e2e7f2",
                    borderRadius: 10,
                    cursor: microsoftLoading || busy ? "default" : "pointer",
                  }}
                >
                  <svg width="17" height="17" viewBox="0 0 23 23" aria-hidden="true">
                    <rect x="1" y="1" width="10" height="10" fill="#f25022" />
                    <rect x="12" y="1" width="10" height="10" fill="#7fba00" />
                    <rect x="1" y="12" width="10" height="10" fill="#00a4ef" />
                    <rect x="12" y="12" width="10" height="10" fill="#ffb900" />
                  </svg>
                  {microsoftLoading
                    ? t("microsoft.login.redirecting", { defaultValue: "Omdirigerer…" })
                    : t("microsoft.login.with_microsoft", {
                        defaultValue: "Logg inn med Microsoft",
                      })}
                </button>
              </>
            )}
          </form>
        </div>
      </div>
    </>
  );
}

export default Login;
